import { GoogleGenAI, Type } from "@google/genai";
import { UsageRecord, GeneratedScenarioResponse } from "../types";
import { db } from "../firebase";
import { collection, addDoc, getDoc, doc, setDoc, getDocs, query, limit, where } from "firebase/firestore";

const apiKey = process.env.API_KEY || '';
const ai = new GoogleGenAI({ apiKey });

// Estimated pricing per 1M tokens (USD)
const PRICING = {
  'gemini-3-flash-preview': { input: 0.10, output: 0.40 },
  'gemini-2.5-flash-image': { input: 0.10, output: 0.40 },
  'gemini-3-pro-preview': { input: 2.00, output: 8.00 },
  'default': { input: 0.20, output: 0.60 }
};

// --- HELPER: Logging ---
const trackUsage = async (model: string, endpoint: string, usageMetadata: any) => {
  if (!usageMetadata) return;

  const inputTokens = usageMetadata.promptTokenCount || 0;
  const outputTokens = usageMetadata.candidatesTokenCount || 0;
  const totalTokens = usageMetadata.totalTokenCount || 0;

  const pricing = PRICING[model as keyof typeof PRICING] || PRICING['default'];
  const cost = (inputTokens / 1_000_000 * pricing.input) + (outputTokens / 1_000_000 * pricing.output);

  const record: UsageRecord = {
    id: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
    model,
    endpoint,
    inputTokens,
    outputTokens,
    totalTokens,
    cost
  };

  try {
    // Save to Firestore for admin analysis
    await addDoc(collection(db, 'usage_logs'), record);
  } catch (e) {
    console.error("Failed to log usage to Firestore", e);
    // Fallback or ignore
  }
};

// --- HELPER: Caching ---
// Tries to get content from Firestore. If missing, runs the generator and saves result.
const getCachedOrGenerate = async (
  collectionName: string, 
  docId: string | null, // If null, we might pick random or just generate
  generatorFn: () => Promise<any>
) => {
  // 1. Strict Cache (if docId provided)
  if (docId) {
    try {
      const docRef = doc(db, collectionName, docId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        console.log(`[Cache Hit] ${collectionName}/${docId}`);
        return docSnap.data();
      }
    } catch (e) { console.warn("Cache read failed", e); }
  }

  // 2. Random Pool Cache (if no docId, usually for scenarios)
  // DEPRECATED FOR SCENARIOS in favor of fetchUnseenScenario
  if (!docId) {
    try {
        // Cost saving: 70% chance to fetch a cached scenario from the pool
        if (Math.random() > 0.3) {
            const q = query(collection(db, collectionName), limit(20));
            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
                const randomIdx = Math.floor(Math.random() * snapshot.size);
                console.log(`[Cache Pool Hit] ${collectionName}`);
                return snapshot.docs[randomIdx].data();
            }
        }
    } catch (e) { console.warn("Cache pool read failed", e); }
  }

  // 3. Generate via AI
  console.log(`[Cache Miss] Generating new content for ${collectionName}...`);
  const content = await generatorFn();

  // 4. Save to Cache
  try {
    if (docId) {
       await setDoc(doc(db, collectionName, docId), content);
    } else {
       await addDoc(collection(db, collectionName), content);
    }
  } catch (e) { console.warn("Cache write failed", e); }

  return content;
};


// System instruction to ensure the AI acts as a Nunchi master
const NUNCHI_MASTER_INSTRUCTION = `
You are a 'Nunchi' Master. Nunchi is the Korean concept of situational awareness, 
often described as 'the art of reading the room'. 
Your goal is to teach users how to assess social hierarchies, mood, and context 
instantaneously to maintain harmony.
`;

/**
 * Pure Generation function for Admin Batch usage or Fallback.
 * Does NOT check cache. Returns the raw object.
 */
export const generateRawScenarioContent = async () => {
    const model = 'gemini-3-flash-preview';
    try {
        const response = await ai.models.generateContent({
        model: model,
        contents: 'Generate a realistic, complex social scenario (e.g., office politics, dating, family dinner, cultural misunderstanding) that requires high Nunchi. Provide 3 reaction options (Great Nunchi, Poor Nunchi, Selfish/No Nunchi).',
        config: {
            systemInstruction: NUNCHI_MASTER_INSTRUCTION,
            responseMimeType: "application/json",
            responseSchema: {
            type: Type.OBJECT,
            properties: {
                scenario: {
                type: Type.OBJECT,
                properties: {
                    title: { type: Type.STRING },
                    setting: { type: Type.STRING },
                    description: { type: Type.STRING, description: "The social situation details." },
                    options: {
                    type: Type.ARRAY,
                    items: {
                        type: Type.OBJECT,
                        properties: {
                        text: { type: Type.STRING, description: "The action the user takes." },
                        nunchiLevel: { type: Type.STRING, enum: ["High", "Low", "None"] },
                        analysis: { type: Type.STRING, description: "Why this is good or bad Nunchi." }
                        },
                        required: ["text", "nunchiLevel", "analysis"]
                    }
                    }
                },
                required: ["title", "setting", "description", "options"]
                }
            }
            }
        }
        });
        
        trackUsage(model, 'generateRawScenarioContent', response.usageMetadata);
        return JSON.parse(response.text || '{}');
    } catch (error) {
        console.error("Gemini Scenario Error:", error);
        throw error;
    }
}

/**
 * Smart fetch for User.
 * 1. Checks 'cached_scenarios'.
 * 2. Filters OUT any IDs present in `seenIds`.
 * 3. Returns the first unseen one.
 * 4. If all seen (or cache empty), generates a fresh one, caches it, and returns it.
 */
export const fetchUnseenScenario = async (seenIds: string[]): Promise<GeneratedScenarioResponse> => {
    const collectionName = 'cached_scenarios';

    // 1. Try to fetch from Firestore pool
    try {
        // Fetch a batch (limit 50 to avoid reading whole DB)
        // Note: Firestore doesn't support "not-in" for large arrays easily. 
        // We fetch a batch and filter client-side.
        const q = query(collection(db, collectionName), limit(50));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
             const allScenarios = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as GeneratedScenarioResponse));
             
             // 2. Filter client-side
             const unseen = allScenarios.filter(s => s.id && !seenIds.includes(s.id));
             
             if (unseen.length > 0) {
                 // Return a random one from the unseen pool
                 const randomIdx = Math.floor(Math.random() * unseen.length);
                 console.log(`[Dojo] Serving cached scenario: ${unseen[randomIdx].id}`);
                 return unseen[randomIdx];
             }
        }
    } catch(e) {
        console.warn("Error fetching scenario pool", e);
    }

    // 3. Fallback: Generate Fresh
    console.log("[Dojo] All cached scenarios seen (or cache empty). Generating fresh...");
    const content = await generateRawScenarioContent();
    
    // 4. Save to Cache
    const docRef = await addDoc(collection(db, collectionName), content);
    
    // Return with ID
    return { ...content, id: docRef.id };
};

// Maintain compatibility for other components calling this
export const generateNunchiScenario = async () => {
  return generateRawScenarioContent();
};

export const generateAssessmentQuiz = async () => {
  const model = 'gemini-3-flash-preview';
  try {
    const response = await ai.models.generateContent({
      model: model,
      contents: 'Generate 5 short multiple-choice situational questions to test a user\'s Nunchi. For each question, provide a brief scenario and 3 options. Assign a score from 0 (Bad Nunchi) to 10 (Master Nunchi) for each option.',
      config: {
        systemInstruction: NUNCHI_MASTER_INSTRUCTION,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            questions: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  scenario: { type: Type.STRING },
                  options: {
                    type: Type.ARRAY,
                    items: {
                      type: Type.OBJECT,
                      properties: {
                        text: { type: Type.STRING },
                        score: { type: Type.NUMBER, description: "Score between 0 and 10" }
                      },
                      required: ["text", "score"]
                    }
                  }
                },
                required: ["scenario", "options"]
              }
            }
          }
        }
      }
    });
    trackUsage(model, 'generateAssessmentQuiz', response.usageMetadata);
    return JSON.parse(response.text || '{}');
  } catch (error) {
    console.error("Gemini Quiz Error:", error);
    throw error;
  }
};

export const analyzeRoomVibe = async (base64Image: string, mimeType: string) => {
  const model = 'gemini-2.5-flash-image';
  try {
    const response = await ai.models.generateContent({
      model: model,
      contents: {
        parts: [
          {
            inlineData: {
              data: base64Image,
              mimeType: mimeType,
            },
          },
          {
            text: "Analyze this image through the lens of 'Nunchi'. Describe the atmosphere/vibe of the room. Identify the power dynamics if visible. What is the 'Kibun' (mood/feeling) of the people? What should someone entering this scene be aware of to maintain harmony?",
          },
        ],
      },
    });
    trackUsage(model, 'analyzeRoomVibe', response.usageMetadata);
    return response.text;
  } catch (error) {
    console.error("Gemini Vision Error:", error);
    throw error;
  }
};

export const generateZenStory = async (theme: string) => {
  const model = 'gemini-3-flash-preview';
  
  // Clean theme for ID
  const docId = theme.toLowerCase().replace(/[^a-z0-9]/g, '_');

  const generator = async () => {
    try {
        const response = await ai.models.generateContent({
        model: model,
        contents: `Generate a short, profound Zen or Taoist story (approx 150-200 words) based on the theme: '${theme}'. It should feel like a classic fable involving monks, nature, or animals (like a monkey). Also provide a one-sentence lesson derived from it.`,
        config: {
            responseMimeType: "application/json",
            responseSchema: {
            type: Type.OBJECT,
            properties: {
                title: { type: Type.STRING },
                content: { type: Type.STRING },
                lesson: { type: Type.STRING }
            },
            required: ["title", "content", "lesson"]
            }
        }
        });
        trackUsage(model, 'generateZenStory', response.usageMetadata);
        return JSON.parse(response.text || '{}');
    } catch (error) {
        console.error("Gemini Zen Story Error:", error);
        throw error;
    }
  };

  return getCachedOrGenerate('cached_zen_stories', docId, generator);
};

export const synthesizeIkigai = async (pillars: { love: string, goodAt: string, worldNeeds: string, paidFor: string }) => {
  const model = 'gemini-3-flash-preview';
  try {
    const response = await ai.models.generateContent({
      model: model,
      contents: `
        Analyze these 4 pillars of a user's Ikigai:
        1. Loves: ${pillars.love}
        2. Good At: ${pillars.goodAt}
        3. World Needs: ${pillars.worldNeeds}
        4. Paid For: ${pillars.paidFor}

        Generate 3 specific career paths, hobbies, or life projects that lie at the intersection of these. 
        Also provide a short "Deep Insight" about their potential purpose.
      `,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            suggestions: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  role: { type: Type.STRING, description: "Job title or Project Name" },
                  description: { type: Type.STRING },
                  matchScore: { type: Type.NUMBER, description: "Percentage match 0-100" },
                  why: { type: Type.STRING, description: "Why this fits their inputs" }
                },
                required: ["role", "description", "matchScore", "why"]
              }
            },
            insight: { type: Type.STRING, description: "A profound observation about their inputs." }
          },
          required: ["suggestions", "insight"]
        }
      }
    });
    trackUsage(model, 'synthesizeIkigai', response.usageMetadata);
    return JSON.parse(response.text || '{}');
  } catch (error) {
    console.error("Gemini Ikigai Synthesis Error:", error);
    throw error;
  }
};

export const generateKoan = async () => {
  const model = 'gemini-3-flash-preview';
  try {
    const response = await ai.models.generateContent({
      model: model,
      contents: 'Generate a profound, classic-style Zen Koan. It should be short, paradoxical, and thought-provoking.',
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            title: { type: Type.STRING },
            text: { type: Type.STRING },
            question: { type: Type.STRING, description: "The specific question the student must meditate on." }
          },
          required: ["title", "text", "question"]
        }
      }
    });
    trackUsage(model, 'generateKoan', response.usageMetadata);
    return JSON.parse(response.text || '{}');
  } catch (error) {
    console.error("Gemini Koan Error:", error);
    throw error;
  }
};

export const evaluateKoanAnswer = async (koan: string, answer: string) => {
  const model = 'gemini-3-flash-preview';
  try {
    const response = await ai.models.generateContent({
      model: model,
      contents: `
        Context Koan: "${koan}"
        Student Answer: "${answer}"
        
        Act as a Zen Master. Evaluate the student's answer. 
        Do not look for "logical" correctness, but for depth, insight, and "Zen" thinking.
        If the answer is intellectual/logical, reject it gently. 
        If it is intuitive/paradoxical/present-moment, praise it.
        Be cryptic but encouraging.
      `,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            feedback: { type: Type.STRING, description: "The master's response to the student." },
            masteryLevel: { type: Type.STRING, enum: ["Grasshopper", "Monk", "Master"] }
          },
          required: ["feedback", "masteryLevel"]
        }
      }
    });
    trackUsage(model, 'evaluateKoanAnswer', response.usageMetadata);
    return JSON.parse(response.text || '{}');
  } catch (error) {
    console.error("Gemini Koan Eval Error:", error);
    throw error;
  }
};

export const cleanseMonkeyMind = async (worry: string) => {
  const model = 'gemini-3-flash-preview';
  try {
    const response = await ai.models.generateContent({
      model: model,
      contents: `
        The user has this worry/stress: "${worry}".
        Act as a Zen Monk. 
        1. Write a short paragraph re-framing this worry using a nature metaphor (leaves floating down a river, clouds passing, wind blowing) to show its impermanence.
        2. Write a Haiku (5-7-5 syllables) summarizing this release.
      `,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            metaphor: { type: Type.STRING },
            haiku: { type: Type.STRING }
          },
          required: ["metaphor", "haiku"]
        }
      }
    });
    trackUsage(model, 'cleanseMonkeyMind', response.usageMetadata);
    return JSON.parse(response.text || '{}');
  } catch (error) {
    console.error("Gemini Mind Cleanse Error:", error);
    throw error;
  }
};

// --- ICHIGO ICHIE SERVICES ---

export const generateTeaCeremony = async (context: string) => {
  const model = 'gemini-3-flash-preview';
  const docId = context.toLowerCase().replace(/[^a-z0-9]/g, '_').substring(0, 50);

  const generator = async () => {
    try {
      const response = await ai.models.generateContent({
        model: model,
        contents: `
          The user is about to experience this interaction/event: "${context}".
          Act as a Japanese Tea Master. Create a mental "Tea Ceremony" guide to help them treat this as a unique, once-in-a-lifetime (Ichigo Ichie) moment.
          Include a theme word (e.g. Harmony, Respect), the atmosphere they should cultivate, 3 actionable steps to be present, and a mindset mantra.
        `,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              theme: { type: Type.STRING, description: "One word theme (Japanese/English)" },
              atmosphere: { type: Type.STRING, description: "Poetic description of the vibe to create" },
              steps: { 
                type: Type.ARRAY, 
                items: { type: Type.STRING },
                description: "3 steps to ground themselves"
              },
              mindset: { type: Type.STRING, description: "A short mantra or thought to hold" }
            },
            required: ["theme", "atmosphere", "steps", "mindset"]
          }
        }
      });
      trackUsage(model, 'generateTeaCeremony', response.usageMetadata);
      return JSON.parse(response.text || '{}');
    } catch (error) {
      console.error("Gemini Tea Ceremony Error:", error);
      throw error;
    }
  };

  return getCachedOrGenerate('cached_tea_ceremonies', docId, generator);
};

export const analyzeMoment = async (memory: string) => {
  const model = 'gemini-3-flash-preview';
  
  // We generally don't cache personal memories publicly for privacy, 
  // but for the sake of the assignment structure we will use a hash or just generate fresh.
  // Here we will generate fresh to be safe with user data, unless it's a very generic prompt.
  
  try {
    const response = await ai.models.generateContent({
      model: model,
      contents: `
        The user provided this memory: "${memory}".
        Analyze this through the lens of Ichigo Ichie (this moment will never happen again).
        1. Give it a poetic title.
        2. Explain its unique significance (why it can never be repeated exactly).
        3. Highlight the sensory details involved.
        4. Write a sentence on the beauty of its impermanence.
      `,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            title: { type: Type.STRING },
            significance: { type: Type.STRING },
            sensoryDetails: { type: Type.STRING },
            impermanence: { type: Type.STRING }
          },
          required: ["title", "significance", "sensoryDetails", "impermanence"]
        }
      }
    });
    trackUsage(model, 'analyzeMoment', response.usageMetadata);
    return JSON.parse(response.text || '{}');
  } catch (error) {
    console.error("Gemini Moment Analysis Error:", error);
    throw error;
  }
};

// --- LETTING GO SERVICES ---

export const generateReleaseGuidance = async (burden: string) => {
  const model = 'gemini-3-flash-preview';
  try {
    const response = await ai.models.generateContent({
      model: model,
      contents: `
        The user is struggling to let go of this burden/attachment: "${burden}".
        Act as a wise, non-judgmental spiritual guide.
        1. Provide a "Perspective Shift" to help them see this differently (e.g. from a place of growth, impermanence, or compassion).
        2. Suggest a simple "Ritual" to mentally or physically release it.
        3. Provide a short, powerful "Affirmation" for freedom.
      `,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            perspective: { type: Type.STRING },
            ritual: { type: Type.STRING },
            affirmation: { type: Type.STRING }
          },
          required: ["perspective", "ritual", "affirmation"]
        }
      }
    });
    trackUsage(model, 'generateReleaseGuidance', response.usageMetadata);
    return JSON.parse(response.text || '{}');
  } catch (error) {
    console.error("Gemini Release Guidance Error:", error);
    throw error;
  }
};

export const generateFlowMeditation = async (feeling: string) => {
  const model = 'gemini-3-flash-preview';
  const docId = feeling.toLowerCase().replace(/[^a-z0-9]/g, '_').substring(0, 30);

  const generator = async () => {
    try {
      const response = await ai.models.generateContent({
        model: model,
        contents: `
          The user is feeling: "${feeling}".
          Create a short guided visualization script to help them "Go with the Flow" and surrender to the moment.
          Choose a natural element (Water, Wind, Earth, Sky) that best fits balancing this emotion.
          Title the visualization.
        `,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              title: { type: Type.STRING },
              visualization: { type: Type.STRING, description: "Approx 100 words of guided imagery." },
              element: { type: Type.STRING }
            },
            required: ["title", "visualization", "element"]
          }
        }
      });
      trackUsage(model, 'generateFlowMeditation', response.usageMetadata);
      return JSON.parse(response.text || '{}');
    } catch (error) {
      console.error("Gemini Flow Meditation Error:", error);
      throw error;
    }
  };

  return getCachedOrGenerate('cached_flow_meditations', docId, generator);
};

// --- COURAGE / ADLERIAN (DISLIKED) SERVICES ---

export const generateTaskSeparation = async (conflict: string) => {
  const model = 'gemini-3-flash-preview';
  
  try {
    const response = await ai.models.generateContent({
      model: model,
      contents: `
        The user has this interpersonal conflict: "${conflict}".
        Act as an Adlerian Philosopher (from "The Courage to Be Disliked").
        Analyze this using "Separation of Tasks".
        1. Identify "Your Task" (what the user controls).
        2. Identify "Their Task" (what the user must let go of).
        3. Provide tough but liberating advice on how to stop interfering.
        4. Provide a short boundary mantra.
      `,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            yourTask: { type: Type.STRING, description: "What the user must do." },
            theirTask: { type: Type.STRING, description: "What belongs to the other person." },
            advice: { type: Type.STRING },
            mantra: { type: Type.STRING }
          },
          required: ["yourTask", "theirTask", "advice", "mantra"]
        }
      }
    });
    trackUsage(model, 'generateTaskSeparation', response.usageMetadata);
    return JSON.parse(response.text || '{}');
  } catch (error) {
    console.error("Gemini Task Separation Error:", error);
    throw error;
  }
};

export const generateTeleologyShift = async (fear: string) => {
  const model = 'gemini-3-flash-preview';
  
  try {
    const response = await ai.models.generateContent({
      model: model,
      contents: `
        The user describes this fear or limitation: "${fear}".
        Act as an Adlerian Philosopher.
        Shift their thinking from Etiology (Past Cause: "I am this way because...") to Teleology (Present Goal: "I am creating this fear to avoid...").
        1. Reveal the "Hidden Goal" of their fear (e.g. avoiding judgment, staying safe).
        2. Reframe the situation to show they have the power to choose differently.
        3. Suggest one courageous action.
      `,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            hiddenGoal: { type: Type.STRING, description: "The purpose the emotion serves." },
            reframe: { type: Type.STRING },
            action: { type: Type.STRING }
          },
          required: ["hiddenGoal", "reframe", "action"]
        }
      }
    });
    trackUsage(model, 'generateTeleologyShift', response.usageMetadata);
    return JSON.parse(response.text || '{}');
  } catch (error) {
    console.error("Gemini Teleology Shift Error:", error);
    throw error;
  }
};

// --- HAPPINESS / ADLERIAN (HAPPY) SERVICES ---

export const generateLoveTaskAnalysis = async (relationshipContext: string) => {
  const model = 'gemini-3-flash-preview';
  try {
    const response = await ai.models.generateContent({
      model: model,
      contents: `
        The user describes this relationship struggle: "${relationshipContext}".
        Act as an Adlerian Philosopher (from "The Courage to Be Happy").
        Focus on the "Task of Love" and the shift from "Me" to "Us".
        1. Explain the "Focus Shift" needed (how to stop seeking status/love and start giving).
        2. Diagnose if the user is treating this as a transaction (credit-based) or unconditional trust.
        3. Provide advice on how to love courageously without demanding a return.
      `,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            focusShift: { type: Type.STRING },
            diagnosis: { type: Type.STRING },
            advice: { type: Type.STRING }
          },
          required: ["focusShift", "diagnosis", "advice"]
        }
      }
    });
    trackUsage(model, 'generateLoveTaskAnalysis', response.usageMetadata);
    return JSON.parse(response.text || '{}');
  } catch (error) {
    console.error("Gemini Love Task Error:", error);
    throw error;
  }
};

export const generateSelfRelianceGuide = async (situation: string) => {
  const model = 'gemini-3-flash-preview';
  try {
    const response = await ai.models.generateContent({
      model: model,
      contents: `
        The user feels dependent or seeks praise in this situation: "${situation}".
        Act as an Adlerian Philosopher.
        Guide them toward "Self-Reliance" (Independence).
        1. Check where they are seeking external validation (Praise).
        2. Provide a Mindset Shift: "My worth is not decided by others."
        3. Give a concrete "Challenge" to act independently.
      `,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            dependencyCheck: { type: Type.STRING },
            mindset: { type: Type.STRING },
            challenge: { type: Type.STRING }
          },
          required: ["dependencyCheck", "mindset", "challenge"]
        }
      }
    });
    trackUsage(model, 'generateSelfRelianceGuide', response.usageMetadata);
    return JSON.parse(response.text || '{}');
  } catch (error) {
    console.error("Gemini Self Reliance Error:", error);
    throw error;
  }
};

// --- KAIZEN SERVICES ---

export const generateSmallSteps = async (goal: string) => {
  const model = 'gemini-3-flash-preview';
  
  try {
    const response = await ai.models.generateContent({
      model: model,
      contents: `
        The user has this big goal: "${goal}".
        Act as a Kaizen Master (Continuous Improvement).
        Break this down into ridiculously small, non-intimidating steps.
        Kaizen means making steps so small you cannot fail.
        1. List 3-5 progressive small steps.
        2. Define the "First Action" - something they can do in less than 1 minute RIGHT NOW (e.g. open the document, put on shoes).
        3. Provide a motivation for why small is better.
      `,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            steps: { type: Type.ARRAY, items: { type: Type.STRING } },
            firstAction: { type: Type.STRING, description: "A micro-action taking < 1 minute" },
            motivation: { type: Type.STRING }
          },
          required: ["steps", "firstAction", "motivation"]
        }
      }
    });
    trackUsage(model, 'generateSmallSteps', response.usageMetadata);
    return JSON.parse(response.text || '{}');
  } catch (error) {
    console.error("Gemini Small Steps Error:", error);
    throw error;
  }
};

export const generateHansei = async (mistake: string) => {
  const model = 'gemini-3-flash-preview';
  
  try {
    const response = await ai.models.generateContent({
      model: model,
      contents: `
        The user made this mistake or failure: "${mistake}".
        Act as a Kaizen Master facilitating "Hansei" (Self-Reflection).
        Hansei is not about regret/shame, but acknowledgment and improvement.
        1. Acknowledgment: Validate the mistake objectively without emotion.
        2. Root Cause: Apply the "5 Whys" thinking to find the deep cause.
        3. Improvement Plan: One concrete action to prevent recurrence.
      `,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            acknowledgment: { type: Type.STRING },
            rootCause: { type: Type.STRING },
            improvement: { type: Type.STRING }
          },
          required: ["acknowledgment", "rootCause", "improvement"]
        }
      }
    });
    trackUsage(model, 'generateHansei', response.usageMetadata);
    return JSON.parse(response.text || '{}');
  } catch (error) {
    console.error("Gemini Hansei Error:", error);
    throw error;
  }
};

// --- STOIC SERVICES (Jonas Salzgeber) ---

export const generateDichotomyAnalysis = async (worry: string) => {
  const model = 'gemini-3-flash-preview';
  
  try {
    const response = await ai.models.generateContent({
      model: model,
      contents: `
        The user is worried about: "${worry}".
        Act as a Stoic Philosopher (inspired by Epictetus and Marcus Aurelius).
        Apply the "Dichotomy of Control".
        1. List what is strictly "In Control" (internal).
        2. List what is "Not In Control" (external).
        3. Provide advice on how to accept the external and focus on the internal.
        4. A short mantra to recite.
      `,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            inControl: { type: Type.ARRAY, items: { type: Type.STRING } },
            notInControl: { type: Type.ARRAY, items: { type: Type.STRING } },
            advice: { type: Type.STRING },
            mantra: { type: Type.STRING }
          },
          required: ["inControl", "notInControl", "advice", "mantra"]
        }
      }
    });
    trackUsage(model, 'generateDichotomyAnalysis', response.usageMetadata);
    return JSON.parse(response.text || '{}');
  } catch (error) {
    console.error("Gemini Dichotomy Error:", error);
    throw error;
  }
};

export const generateStoicMorning = async (plans: string) => {
  const model = 'gemini-3-flash-preview';
  
  try {
    const response = await ai.models.generateContent({
      model: model,
      contents: `
        The user has these plans for the day: "${plans}".
        Act as a Stoic Philosopher facilitating "Premeditatio Malorum" (Negative Visualization).
        1. Forecast potential "Adversities" or annoyances (traffic, rude people, failure).
        2. Suggest specific "Virtues" (Wisdom, Justice, Courage, Temperance) to apply to these situations.
        3. Create a strong "Intention" statement for the day.
      `,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            forecast: { type: Type.STRING },
            virtues: { type: Type.ARRAY, items: { type: Type.STRING } },
            intention: { type: Type.STRING }
          },
          required: ["forecast", "virtues", "intention"]
        }
      }
    });
    trackUsage(model, 'generateStoicMorning', response.usageMetadata);
    return JSON.parse(response.text || '{}');
  } catch (error) {
    console.error("Gemini Stoic Morning Error:", error);
    throw error;
  }
};

// --- SLOW DOWN SERVICES (Haemin Sunim) ---

export const generateMindfulPause = async (stressor: string) => {
  const model = 'gemini-3-flash-preview';
  
  try {
    const response = await ai.models.generateContent({
      model: model,
      contents: `
        The user feels overwhelmed by: "${stressor}".
        Act as Haemin Sunim (author of The Things You Can See Only When You Slow Down).
        Help them hit the "Pause Button".
        1. Perspective: Offer a gentle thought to slow down their racing mind.
        2. Action: A simple, immediate action to find stillness (e.g. breathe, observe).
        3. Wisdom: A short, poetic quote about rest or timing.
      `,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            perspective: { type: Type.STRING },
            action: { type: Type.STRING },
            wisdom: { type: Type.STRING }
          },
          required: ["perspective", "action", "wisdom"]
        }
      }
    });
    trackUsage(model, 'generateMindfulPause', response.usageMetadata);
    return JSON.parse(response.text || '{}');
  } catch (error) {
    console.error("Gemini Pause Error:", error);
    throw error;
  }
};

export const generateRelationshipHarmony = async (conflict: string) => {
  const model = 'gemini-3-flash-preview';
  
  try {
    const response = await ai.models.generateContent({
      model: model,
      contents: `
        The user has this relationship difficulty: "${conflict}".
        Act as Haemin Sunim. Provide advice based on empathy and "lowering your guard".
        1. Empathy: Help the user see the other person's suffering or perspective.
        2. Advice: Gentle guidance on how to respond with love rather than rightness.
        3. Reflection: What this situation teaches about the user's own mind.
      `,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            empathy: { type: Type.STRING },
            advice: { type: Type.STRING },
            reflection: { type: Type.STRING }
          },
          required: ["empathy", "advice", "reflection"]
        }
      }
    });
    trackUsage(model, 'generateRelationshipHarmony', response.usageMetadata);
    return JSON.parse(response.text || '{}');
  } catch (error) {
    console.error("Gemini Relationship Harmony Error:", error);
    throw error;
  }
};